import { DEFAULT_SEND_TIME } from "@/constants/campaignBuilder";
import { formatDisplayDate, parseTime } from "@/lib/reminders";
import { isoToZonedSlot, scheduleZoneName, zonedWallTimeToIso } from "@/utils/timezone-helper";

/**
 * Per-recipient send dates ("Send on different dates"). The API reads a recipient's `sendDate` as
 * `YYYY-MM-DD` and `sendTime` as `HH:mm` in the campaign's zone, and nothing else: the importer's
 * `11/28/2026` used to reach it raw, fail to parse and save the contact undated, i.e. sent at launch
 * (GFG-443). Every entry point (bulk import, Add Manually, the table's cell edit) normalizes here.
 */

interface SendSchedule {
  sendDate?: string;
  sendTime?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

function calendarDate(year: number, month: number, day: number): string | null {
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * A typed or imported send date → `YYYY-MM-DD`. Reads ISO (`2026-11-28`, `2026/11/28`, a trailing
 * time is ignored) and US month-first (`11/28/2026`, `1/5/27`, `-` or `.` separators). Blank is
 * undefined; anything else, or a day not on the calendar, is null.
 */
export function normalizeSendDate(raw: string | undefined): string | undefined | null {
  const value = raw?.trim() ?? "";
  if (!value) return undefined;
  const iso = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?$/.exec(value);
  if (iso) return calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const us = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(value);
  if (!us) return null;
  const year = us[3]?.length === 2 ? 2000 + Number(us[3]) : Number(us[3]);
  return calendarDate(year, Number(us[1]), Number(us[2]));
}

/**
 * A typed or imported send time → 24h `HH:mm`. Reads `9:00`, `17:30:00`, `9:00 AM`, `5:30pm`,
 * `9am`. Blank is undefined; anything else is null.
 */
export function normalizeSendTime(raw: string | undefined): string | undefined | null {
  const value = raw?.trim().toLowerCase() ?? "";
  if (!value) return undefined;
  const m = /^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(am|pm)?$/.exec(value);
  if (!m) return null;
  const minute = Number(m[2] ?? "0");
  let hour = Number(m[1]);
  const meridiem = m[3];
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === "pm" ? 12 : 0);
  } else if (m[2] === undefined) {
    // A bare number ("9") is too ambiguous to guess at.
    return null;
  }
  if (hour > 23 || minute > 59) return null;
  return `${pad(hour)}:${pad(minute)}`;
}

/** Any row carrying a non-blank send date or time. */
export function hasOwnSendSchedule(rows: readonly SendSchedule[]): boolean {
  return rows.some((r) => !!r.sendDate?.trim() || !!r.sendTime?.trim());
}

/** The rows without their send date/time: the sender kept a campaign-level schedule instead. */
export function stripSendSchedule<T extends SendSchedule>(rows: readonly T[]): T[] {
  return rows.map(({ sendDate: _date, sendTime: _time, ...rest }) => rest as T);
}

/** What the defaults changed, for the alert shown after an import or a manual add. */
export interface SendScheduleNotice {
  /** Date given, no time: set to 9:00 AM in the campaign's zone. */
  defaultedTime: number;
  /** Time given, no date: set to the next calendar day. */
  defaultedDate: number;
  /** A date or time that could not be read (counted as missing, then defaulted). */
  unreadable: number;
  /** A send date already past: the recipient is sent as soon as the campaign launches. */
  past: number;
  /** Neither date nor time: the recipient is sent as soon as the campaign launches. */
  undated: number;
}

interface ScheduleContext {
  /** The campaign's IANA zone: the zone the API reads every per-recipient date in. */
  timeZone: string;
  now: Date;
}

/** The next calendar day after today, in the campaign's zone. */
function tomorrowIn(ctx: ScheduleContext): string {
  const today = isoToZonedSlot(ctx.now.toISOString(), ctx.timeZone).date;
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/**
 * Normalize each row's send date/time and fill the missing half (GFG-443): a date alone is sent at
 * 9:00 AM in the campaign's zone, the rule the API applies everywhere (Ivan + Will on PR #539); a
 * time alone is sent on the next calendar day. Rows keep every other field.
 */
export function applySendScheduleDefaults<T extends SendSchedule>(
  rows: readonly T[],
  ctx: ScheduleContext,
): { recipients: T[]; notice: SendScheduleNotice } {
  const notice: SendScheduleNotice = {
    defaultedTime: 0,
    defaultedDate: 0,
    unreadable: 0,
    past: 0,
    undated: 0,
  };
  const tomorrow = tomorrowIn(ctx);
  const recipients = rows.map((row) => {
    const { sendDate: rawDate, sendTime: rawTime, ...rest } = row;
    const parsedDate = normalizeSendDate(rawDate);
    const parsedTime = normalizeSendTime(rawTime);
    if (parsedDate === null || parsedTime === null) notice.unreadable++;
    let date = parsedDate ?? undefined;
    let time = parsedTime ?? undefined;
    if (!date && !time) {
      notice.undated++;
      return rest as T;
    }
    if (!date) {
      date = tomorrow;
      notice.defaultedDate++;
    }
    if (!time) {
      time = DEFAULT_SEND_TIME;
      notice.defaultedTime++;
    }
    const at = zonedWallTimeToIso(date, time, ctx.timeZone);
    if (at && new Date(at).getTime() <= ctx.now.getTime()) notice.past++;
    return { ...rest, sendDate: date, sendTime: time } as T;
  });
  return { recipients, notice };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** `14:15` → `2:15 PM`, on the reminders' own parser. */
function formatTime12h(time: string): string {
  const { h, m, ap } = parseTime(time);
  return `${Number(h)}:${m} ${ap}`;
}

/** The alert's sentences, one per thing the defaults did; empty when nothing needs saying. */
export function sendScheduleNoticeLines(
  notice: SendScheduleNotice,
  ctx: ScheduleContext,
): string[] {
  const forN = (n: number) => plural(n, "recipient", "recipients");
  const lines: string[] = [];
  if (notice.unreadable > 0) {
    lines.push(
      `Couldn't read the send date or time for ${forN(notice.unreadable)}: treated as blank.`,
    );
  }
  if (notice.defaultedTime > 0) {
    const at = `${formatTime12h(DEFAULT_SEND_TIME)} ${scheduleZoneName(ctx.timeZone)}`;
    lines.push(`No send time for ${forN(notice.defaultedTime)}: set to ${at}.`);
  }
  if (notice.defaultedDate > 0) {
    lines.push(
      `No send date for ${forN(notice.defaultedDate)}: set to tomorrow, ${formatDisplayDate(tomorrowIn(ctx))}.`,
    );
  }
  if (notice.past > 0) {
    lines.push(
      `Send date already past for ${forN(notice.past)}: sent as soon as the campaign launches.`,
    );
  }
  if (notice.undated > 0) {
    lines.push(
      `No send date or time for ${forN(notice.undated)}: sent as soon as the campaign launches.`,
    );
  }
  return lines;
}

/** The alert line for a send date/time typed into the table that cannot be read: it is not saved. */
export function unreadableSendEditLine(field: "sendDate" | "sendTime", raw: string): string {
  const example = field === "sendDate" ? "11/28/2026 or 2026-11-28" : "9:30 AM or 14:30";
  const what = field === "sendDate" ? "send date" : "send time";
  return `Couldn't read "${raw.trim()}" as a ${what}, so it was not saved. Try ${example}.`;
}
